// Inventory Health Monitor — one page, 6 tabs + Overview + ASIN drawer.
// Each tab hits its own endpoint; settings sit on a modal that any tab can open.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import * as api from './services/inventoryHealthApi';
import styles from './InventoryHealth.module.css';

// ── Shared helpers ──────────────────────────────────────────────────────────

// Entry points into the other inventory surfaces (Restock #44, IPM #42).
const RESTOCK = '/dashboard/seller-intelligence/restock';
const IPM = '/dashboard/seller-intelligence/inventory-performance';
function GoLink({ to, children }) {
  const navigate = useNavigate();
  return (
    <button className={styles.linkBtn}
            onClick={(e) => { e.stopPropagation(); navigate(to); }}>
      {children}
    </button>
  );
}
const NEEDS_ORDER = ['low', 'warning', 'critical', 'out_of_stock'];

// #61 Value is at cost from Profit & Margin; SKUs without a cost are blank.
const COST_SETUP = '/dashboard/seller-intelligence/cost-setup';
function CostCoverageBanner({ coverage }) {
  const navigate = useNavigate();
  if (!coverage) return null;
  const total = coverage.skus_with_price + coverage.skus_missing_price;
  if (coverage.units_only_mode) {
    return (
      <div className={`${styles.banner} ${styles.bannerWarn}`}>
        No costs entered in Profit &amp; Margin, so inventory is shown in units only.{' '}
        <button className={styles.linkBtn} onClick={() => navigate(COST_SETUP)}>Add costs →</button>
      </div>
    );
  }
  if (!coverage.coverage_incomplete) return null;
  return (
    <div className={`${styles.banner} ${styles.bannerAge}`}>
      Valued at cost from Profit &amp; Margin for {coverage.skus_with_price} of {total} SKUs.
      The other {coverage.skus_missing_price} have no cost entered, so their value is blank, not zero.{' '}
      <button className={styles.linkBtn} onClick={() => navigate(COST_SETUP)}>Add costs →</button>
    </div>
  );
}

const INR = (paise) => {
  if (paise == null) return '—';
  const rupees = paise / 100;
  if (rupees >= 10_000_000) return `₹${(rupees / 10_000_000).toFixed(2)} Cr`;
  if (rupees >= 100_000) return `₹${(rupees / 100_000).toFixed(2)} L`;
  return `₹${Math.round(rupees).toLocaleString('en-IN')}`;
};

const stateChipCls = (state) => {
  switch (state) {
    case 'critical': case 'out_of_stock': return styles.stateCritical;
    case 'warning': return styles.stateWarning;
    case 'low': return styles.stateLow;
    case 'healthy': return styles.stateHealthy;
    case 'dormant': case 'dead_or_near_dead': return styles.stateDormant;
    case 'excess': return styles.stateExcess;
    case 'running_lean': return styles.stateRunningLean;
    case 'below': return styles.stateBelow;
    case 'unmeasured': case 'mfn_no_corridor': return styles.stateUnmeasured;
    default: return styles.stateNeutral;
  }
};

const StateChip = ({ state }) => (
  <span className={`${styles.chip} ${stateChipCls(state)}`}>
    {(state || 'unknown').replace(/_/g, ' ')}
  </span>
);

const useEndpoint = (fetchFn) => {
  const authCtx = useContext(AuthContext);
  const [state, setState] = useState({ loading: true, data: null, freshness: null, error: null });
  const reload = useCallback(async () => {
    if (!authCtx?.token) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const body = await fetchFn(authCtx.token);
      setState({ loading: false, data: body.data, freshness: body.freshness, error: null });
    } catch (e) {
      setState({ loading: false, data: null, freshness: null, error: e.message });
    }
  }, [authCtx?.token, fetchFn]);
  useEffect(() => { reload(); }, [reload]);
  return { ...state, reload };
};

// ── Overview (Depth 1) ─────────────────────────────────────────────────────

const OverviewPanel = ({ openSettings, onSliceClick }) => {
  const { loading, data, error } = useEndpoint(api.getOverview);
  if (loading && !data) return <div className={styles.muted}>Loading overview…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const { is_cold_start, capital_position, state_totals, problem_slices, surface_note } = data;
  return (
    <div>
      {is_cold_start && (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          Just connected — snapshots are still building. Coverage days and trend charts fill in
          once we have a few days of data. Nothing broken; just early.
        </div>
      )}
      <CostCoverageBanner coverage={surface_note?.value_coverage} />

      <div className={styles.tileGrid}>
        <Tile label="Total inventory value"
              value={capital_position.total_value_minor != null ? INR(capital_position.total_value_minor) : '—'}
              accent="ok"
              hint="at cost (Profit & Margin)" />
        <Tile label="Catalogue days of cover"
              value={capital_position.catalogue_days_of_cover != null ? `${capital_position.catalogue_days_of_cover}d` : '—'} />
        <Tile label="Total SKUs" value={capital_position.total_skus} />
        <Tile label="Total units" value={capital_position.total_units.toLocaleString('en-IN')} />
        <Tile label="Healthy" value={state_totals.healthy} accent="ok" />
        <Tile label="At risk"
              value={(state_totals.critical + state_totals.warning + state_totals.low + state_totals.out_of_stock)}
              accent="warn" />
        <Tile label="Unmeasured"
              value={state_totals.unmeasured}
              hint="Need more sales history to classify" />
      </div>

      <h4 className={styles.sectionTitle}>Problem slices — every line is a doorway</h4>
      <div className={styles.sliceList}>
        {problem_slices.filter((s) => s.sku_count > 0).map((s, i) => (
          <div key={i} className={styles.sliceRow}
               onClick={() => onSliceClick(s.filter, s.route_to)}
               role="button" tabIndex={0}>
            <div className={styles.sliceMain}>
              <div className={styles.sliceLabel}>{s.label}</div>
              <div className={styles.sliceHint}>{s.route_hint}</div>
            </div>
            <div className={styles.sliceValues}>
              {s.value_minor > 0 && (
                <span className={styles.sliceValue}>{INR(s.value_minor)}</span>
              )}
              <span className={styles.sliceCount}>{s.sku_count} SKU{s.sku_count === 1 ? '' : 's'}</span>
              <span className={styles.chevron}>›</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Sub-panels ──────────────────────────────────────────────────────────────

const CurrentLevelsPanel = ({ onOpenAsin }) => {
  const { loading, data, error } = useEndpoint(api.getCurrentLevels);
  const [expanded, setExpanded] = useState(new Set());

  if (loading && !data) return <div className={styles.muted}>Loading current levels…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;

  const { totals, asin_count, zero_fulfillable_count, coverage, asins } = data;
  const toggle = (asin) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(asin)) next.delete(asin); else next.add(asin);
    return next;
  });

  return (
    <div>
      {coverage?.coverage_incomplete && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          Coverage incomplete: {coverage.missing_inventory_skus.length} SKU(s) are in your listings
          but have no inventory row. Amazon may still be reporting on them.
        </div>
      )}

      <div className={styles.tileGrid}>
        <Tile label="Fulfillable" value={totals.fulfillable.toLocaleString('en-IN')} />
        <Tile label="Reserved" value={totals.reserved.toLocaleString('en-IN')}
              hint="Kept distinct from Unfulfillable — Reserved needs no action" />
        <Tile label="Unfulfillable" value={totals.unfulfillable.toLocaleString('en-IN')}
              hint="Damaged / expired / defective — action: removal" />
        <Tile label="Researching" value={totals.researching.toLocaleString('en-IN')} />
        <Tile label="Inbound (working)" value={totals.inbound_working.toLocaleString('en-IN')} />
        <Tile label="Inbound (shipped)" value={totals.inbound_shipped.toLocaleString('en-IN')} />
        <Tile label="Inbound (receiving)" value={totals.inbound_receiving.toLocaleString('en-IN')} />
        <Tile label="Zero-fulfillable SKUs" value={zero_fulfillable_count}
              accent={zero_fulfillable_count > 0 ? 'warn' : null}
              hint="Surfaced, not buried (PRD #6)" />
      </div>

      <h4 className={styles.sectionTitle}>Per ASIN ({asin_count})</h4>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ASIN</th><th>Fulfillable</th><th>Reserved</th>
              <th>Inbound (w/s/r)</th><th>Unfulfillable</th><th>SKUs</th><th />
            </tr>
          </thead>
          <tbody>
            {asins.slice(0, 200).map((a) => (
              <React.Fragment key={a.asin}>
                <tr className={styles.clickable}>
                  <td className={styles.mono} onClick={() => onOpenAsin?.(a.asin)}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    {a.asin}
                  </td>
                  <td onClick={() => toggle(a.asin)}>{a.fulfillable}</td>
                  <td onClick={() => toggle(a.asin)}>{a.reserved}</td>
                  <td className={styles.mono} onClick={() => toggle(a.asin)}>
                    {a.inbound_working}/{a.inbound_shipped}/{a.inbound_receiving}
                  </td>
                  <td onClick={() => toggle(a.asin)}>{a.unfulfillable}</td>
                  <td onClick={() => toggle(a.asin)}>{a.skus.length}</td>
                  <td onClick={() => toggle(a.asin)}>{expanded.has(a.asin) ? '▾' : '▸'}</td>
                </tr>
                {expanded.has(a.asin) && a.skus.map((s) => (
                  <tr key={s.sku} className={styles.childRow}>
                    <td className={styles.mono}>&nbsp;&nbsp;└ {s.sku}
                      {s.seller_declared && <span className={styles.pillMuted}>seller-declared</span>}
                    </td>
                    <td>{s.fulfillable}{s.zero_fulfillable && <span className={styles.pillWarn}>0</span>}</td>
                    <td>{s.reserved}</td>
                    <td className={styles.mono}>
                      {s.inbound.working}/{s.inbound.shipped}/{s.inbound.receiving}
                    </td>
                    <td>{s.unfulfillable}</td>
                    <td>{s.fulfillment_channel}</td>
                    <td />
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StockOutRiskPanel = ({ openSettings, onOpenAsin, initialStateFilter }) => {
  const { loading, data, error } = useEndpoint(api.getStockOutRisk);
  const [filter, setFilter] = useState(initialStateFilter || 'all');
  if (loading && !data) return <div className={styles.muted}>Loading stock-out risk…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;

  const { totals, at_risk_count, assumptions, rows } = data;
  const filteredRows = filter === 'all' ? rows : rows.filter((r) => r.state === filter);

  return (
    <div>
      {assumptions.prompt_set_lead_time && (
        <div className={`${styles.banner} ${styles.bannerInfo}`}>
          No lead time set — states use a {assumptions.lead_time_days_fba}-day default.{' '}
          <button className={styles.linkBtn} onClick={openSettings}>Set lead time →</button>
        </div>
      )}

      <div className={styles.tileGrid}>
        <Tile label="Critical" value={totals.critical} accent="critical" />
        <Tile label="Warning" value={totals.warning} accent="warn" />
        <Tile label="Low" value={totals.low} accent="warn" />
        <Tile label="Out of stock" value={totals.out_of_stock} accent="critical" />
        <Tile label="Healthy" value={totals.healthy} accent="ok" />
        <Tile label="Dormant" value={totals.dormant} />
        <Tile label="Unmeasured" value={totals.unmeasured} hint="Under 14 buyable days or 30 units" />
        <Tile label="At risk (total)" value={at_risk_count} accent="warn" />
      </div>

      <div className={styles.assumptions}>
        Assumptions:{' '}
        <Assumption label="Lead time (FBA)" value={`${assumptions.lead_time_days_fba}d`}
                    isDefault={assumptions.is_default.lead_time_days_fba} />
        <Assumption label="Safety buffer" value={`${assumptions.safety_buffer_days}d`}
                    isDefault={assumptions.is_default.safety_buffer_days} />
        <Assumption label="Ordering window" value={`${assumptions.ordering_window_days}d`}
                    isDefault={assumptions.is_default.ordering_window_days} />
        <button className={styles.linkBtn} onClick={openSettings}>Adjust →</button>
      </div>

      <div className={styles.tableToolbar}>
        <label>
          Filter by state:
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All ({rows.length})</option>
            {['critical', 'warning', 'low', 'out_of_stock', 'healthy',
              'dormant', 'unmeasured'].map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')} ({rows.filter((r) => r.state === s).length})
              </option>
            ))}
          </select>
        </label>
        {initialStateFilter && (
          <span className={styles.filterHint}>
            Filtered from Overview slice
          </span>
        )}
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>SKU</th><th>ASIN</th><th>Channel</th><th>State</th>
              <th>Cover</th><th>Landing</th><th>Margin</th>
              <th>Velocity</th><th>Trend</th><th>Confidence</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.slice(0, 200).map((r) => (
              <tr key={r.sku}
                  className={r.asin ? styles.clickable : ''}
                  onClick={() => r.asin && onOpenAsin?.(r.asin)}>
                <td className={styles.mono}>{r.sku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>{r.fulfillment_channel}</td>
                <td><StateChip state={r.state} /></td>
                <td>{r.days_of_coverage != null ? `${r.days_of_coverage}d` : '—'}</td>
                <td>{r.landing_date || '—'}</td>
                <td>{r.margin_days != null ? (r.margin_days >= 0 ?
                  <span className={styles.marginOk}>+{r.margin_days}d</span> :
                  <span className={styles.marginBad}>{r.margin_days}d</span>) : '—'}</td>
                <td>{r.velocity_recent != null ? r.velocity_recent.toFixed(2) : '—'}</td>
                <td className={styles.trend}>
                  {r.trend === 'up' && '↑'}
                  {r.trend === 'down' && '↓'}
                  {r.trend === 'flat' && '→'}
                  {r.trend === 'unknown' && '?'}
                </td>
                <td>{r.confidence || '—'}</td>
                <td>{r.route_to_restock && (
                  <GoLink to={`${RESTOCK}?sku=${encodeURIComponent(r.sku)}`}>Restock →</GoLink>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DaysOnHandPanel = () => {
  const { loading, data, error } = useEndpoint(api.getDaysOnHand);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;

  const { catalogue_days_of_cover, average, distribution, by_channel, trend } = data;
  return (
    <div>
      <div className={styles.tileGrid}>
        <Tile label="Catalogue days of cover" value={catalogue_days_of_cover != null ? `${catalogue_days_of_cover}d` : '—'}
              accent="ok" />
        <Tile label="Average (per SKU)" value={average != null ? `${average}d` : '—'} />
        <Tile label="Sample size" value={distribution.sample_size} />
        {Object.entries(by_channel).map(([ch, s]) => (
          <Tile key={ch} label={`${ch} days of cover`}
                value={s.days_of_cover != null ? `${s.days_of_cover}d` : '—'}
                hint={`${s.units.toLocaleString()} units · ${s.daily_velocity.toFixed(1)} u/day`} />
        ))}
      </div>

      <h4 className={styles.sectionTitle}>Distribution</h4>
      <div className={styles.percentileRow}>
        {['p10', 'p25', 'p50', 'p75', 'p90'].map((p) => (
          <div key={p} className={styles.percentile}>
            <div className={styles.percentileLabel}>{p.toUpperCase()}</div>
            <div className={styles.percentileValue}>
              {distribution[p] != null ? `${distribution[p]}d` : '—'}
            </div>
          </div>
        ))}
      </div>

      {!trend.supported && (
        <div className={`${styles.banner} ${styles.bannerAge}`}>
          Trend chart pending — daily snapshot history begins accumulating from today.
        </div>
      )}
    </div>
  );
};

const TurnoverPanel = ({ onOpenAsin }) => {
  const { loading, data, error } = useEndpoint(api.getTurnoverCorridor);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;

  const { window_days, band_counts, rows, definition } = data;
  return (
    <div>
      <div className={styles.definition}>
        <b>Turnover</b> = {definition.formula} over {window_days}d.{' '}
        Held basis: {definition.held_basis}.
        {definition.mfn_corridor_anchor && (
          <>{' '}MFN corridor: {definition.mfn_corridor_anchor}.</>
        )}
      </div>
      {definition.long_cycle_active && (
        <div className={`${styles.banner} ${styles.bannerWarn}`}>
          Your replenishment cycle exceeds {definition.long_cycle_threshold_days} days —
          the turnover corridor doesn't apply. The fix is the cycle length,
          not the inventory level.
        </div>
      )}
      <div className={styles.tileGrid}>
        <Tile label="Dead / near-dead" value={band_counts.dead_or_near_dead || 0} accent="critical" />
        <Tile label="Excess" value={band_counts.excess || 0} accent="warn" />
        <Tile label="Below corridor" value={band_counts.below || 0} />
        <Tile label="Healthy" value={band_counts.healthy || 0} accent="ok" />
        <Tile label="Running lean" value={band_counts.running_lean || 0} accent="warn" />
        <Tile label="MFN (no corridor)" value={band_counts.mfn_no_corridor || 0}
              hint="MFN has no Amazon denominator" />
        <Tile label="Unmeasured" value={band_counts.unmeasured || 0} />
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU</th><th>ASIN</th><th>Channel</th><th>Band</th>
                <th>Turnover</th><th>Annualised</th><th>Units sold</th><th>Held</th><th></th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r) => (
              <tr key={r.sku}
                  className={r.asin ? styles.clickable : ''}
                  onClick={() => r.asin && onOpenAsin?.(r.asin)}>
                <td className={styles.mono}>{r.sku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>{r.fulfillment_channel}</td>
                <td>
                  <StateChip state={r.state} />
                  {r.sub_state && (
                    <div className={styles.subState}>
                      → {r.sub_state.replace('_', ' ')}
                    </div>
                  )}
                </td>
                <td>{r.turnover != null ? r.turnover.toFixed(2) : '—'}</td>
                <td>{r.annualised != null ? r.annualised.toFixed(2) : '—'}</td>
                <td>{r.units_sold}</td>
                <td>{r.held}</td>
                <td>{r.state === 'excess' && (
                  <GoLink to={`${IPM}?tab=excess`}>Excess →</GoLink>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DeadStockPanel = ({ onOpenAsin }) => {
  const { loading, data, error } = useEndpoint(api.getDeadStockReport);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;

  const {
    total_dead_skus, total_dead_value_minor, window_days,
    classification_counts = {}, seasonal_join_available, rows,
  } = data;
  return (
    <div>
      {!seasonal_join_available && (
        <div className={`${styles.banner} ${styles.bannerAge}`}>
          Seasons haven't been worked out for this account yet, so a seasonal product
          resting between seasons may show as Dead rather than Dormant.
        </div>
      )}
      <div className={styles.tileGrid}>
        <Tile label="Dead SKUs" value={total_dead_skus} accent="critical"
              hint={`Zero velocity for ${window_days} days with stock`} />
        <Tile label="Locked value" value={INR(total_dead_value_minor)} accent="critical"
              hint={data.dead_skus_without_cost
                ? `${data.dead_skus_without_cost} of ${total_dead_skus} have no cost in Profit & Margin`
                : 'at cost'} />
        <Tile label="Stranded" value={classification_counts.stranded || 0}
              hint="No active listing (fix listing first)" />
        <Tile label="Unproven" value={classification_counts.unproven || 0}
              hint="Too new to call dead" />
        <Tile label="Dormant" value={classification_counts.dormant || 0}
              hint="Seasonal, between seasons — not dead" />
        <Tile label="Clearing" value={classification_counts.clearing || 0}
              hint="Seller-tagged for clearance" />
        <Tile label="Unfulfillable only" value={classification_counts.unfulfillable_only || 0}
              accent="warn" hint="Only removable stock left" />
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU</th><th>ASIN</th><th>Channel</th><th>State</th>
                <th>On hand</th><th>Unit cost</th><th>Value</th>
                <th title="Storage cost per month at the current rate">Storage/mo</th>
                <th title="Days until Amazon's long-term storage surcharge starts">
                  Days to LTS</th>
                <th>Action</th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r) => (
              <tr key={r.sku}
                  className={r.asin ? styles.clickable : ''}
                  onClick={() => r.asin && onOpenAsin?.(r.asin)}>
                <td className={styles.mono}>{r.sku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>{r.fulfillment_channel}</td>
                <td><StateChip state={r.state} /></td>
                <td>
                  {r.on_hand}
                  {r.on_hand_unfulfillable > 0 && (
                    <span className={styles.pillWarn}>
                      {r.on_hand_unfulfillable} unfulfillable
                    </span>
                  )}
                </td>
                <td>{INR(r.unit_price_minor)}</td>
                <td className={styles.valueBold}>{INR(r.value_minor)}</td>
                <td>{INR(r.storage_cost_current_minor_per_month)}
                  {r.storage_cost_long_term_minor_per_month > r.storage_cost_current_minor_per_month && (
                    <div className={styles.tileHint}>
                      →{INR(r.storage_cost_long_term_minor_per_month)} @ LTS
                    </div>
                  )}
                </td>
                <td>{r.days_to_long_term_storage != null ? `${r.days_to_long_term_storage}d` : '—'}</td>
                <td>
                  {r.route_to === 'removal_order' && <span className={styles.pillMuted}>Removal</span>}
                  {r.route_to === 'listing_repair' && (
                    <GoLink to={`${IPM}?tab=stranded`}>Stranded →</GoLink>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ValueReportPanel = ({ onOpenAsin }) => {
  const { loading, data, error } = useEndpoint(api.getValueReport);
  if (loading && !data) return <div className={styles.muted}>Loading…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data) return null;
  const {
    totals, by_channel, by_inventory_state = {}, by_health_state = {},
    top_by_value, coverage,
  } = data;
  return (
    <div>
      <CostCoverageBanner coverage={coverage} />
      <div className={styles.tileGrid}>
        <Tile label="Total inventory value"
              value={totals.value_minor != null ? INR(totals.value_minor) : '—'}
              accent="ok"
              hint="at cost (Profit & Margin)" />
        <Tile label="Total units" value={totals.units.toLocaleString('en-IN')} />
        <Tile label="Total SKUs" value={totals.skus} />
      </div>

      <h4 className={styles.sectionTitle}>By channel</h4>
      <div className={styles.tileGrid}>
        {Object.entries(by_channel).map(([ch, c]) => (
          <Tile key={ch} label={`${ch} value`} value={INR(c.value)}
                hint={
                  <>
                    {c.skus} SKUs · {c.units.toLocaleString()} units
                    {c.seller_declared && <><br/><span className={styles.pillMuted}>seller-declared</span></>}
                  </>
                } />
        ))}
      </div>

      <h4 className={styles.sectionTitle}>By inventory state</h4>
      <div className={styles.tileGrid}>
        {Object.entries(by_inventory_state).map(([st, v]) => (
          <Tile key={st} label={st.charAt(0).toUpperCase() + st.slice(1)}
                value={INR(v.value_minor)}
                hint={`${v.units.toLocaleString()} units`} />
        ))}
      </div>

      <h4 className={styles.sectionTitle}>By health state (mutually exclusive — no double-counting)</h4>
      <div className={styles.tileGrid}>
        {Object.entries(by_health_state).map(([st, v]) => (
          <Tile key={st} label={st.replace(/_/g, ' ')}
                value={INR(v.value_minor)}
                accent={
                  ['critical', 'out_of_stock'].includes(st) ? 'critical' :
                  ['warning', 'low'].includes(st) ? 'warn' :
                  st === 'healthy' ? 'ok' : null
                }
                hint={`${v.skus} SKUs`} />
        ))}
      </div>

      <h4 className={styles.sectionTitle}>Top by value</h4>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr><th>SKU</th><th>ASIN</th><th>Channel</th>
                <th>On hand</th><th>Unit cost</th><th>Value</th></tr>
          </thead>
          <tbody>
            {top_by_value.map((r) => (
              <tr key={r.sku}
                  className={r.asin ? styles.clickable : ''}
                  onClick={() => r.asin && onOpenAsin?.(r.asin)}>
                <td className={styles.mono}>{r.sku}</td>
                <td className={styles.mono}>{r.asin || '—'}</td>
                <td>{r.fulfillment_channel}</td>
                <td>{r.on_hand}</td>
                <td>{r.unit_price_minor != null ? INR(r.unit_price_minor) : '—'}</td>
                <td className={styles.valueBold}>
                  {r.value_minor != null ? INR(r.value_minor) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Reusable bits ───────────────────────────────────────────────────────────

const Tile = ({ label, value, hint, accent }) => (
  <div className={`${styles.tile} ${accent ? styles[`tile_${accent}`] : ''}`}>
    <div className={styles.tileLabel}>{label}</div>
    <div className={styles.tileValue}>{value ?? '—'}</div>
    {hint && <div className={styles.tileHint}>{hint}</div>}
  </div>
);

const Assumption = ({ label, value, isDefault }) => (
  <span className={styles.assumption}>
    {label}: <b>{value}</b>
    {isDefault && <span className={styles.pillMuted}>assumed</span>}
  </span>
);

// ── ASIN detail drawer (Depth 3, #68) ──────────────────────────────────────

const AsinDrawer = ({ asin, onClose }) => {
  const authCtx = useContext(AuthContext);
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await api.getAsinDetail(authCtx.token, asin);
        if (!cancelled) setState({ loading: false, data: body.data, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, [authCtx.token, asin]);

  const { loading, data, error } = state;
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div>
            <h3>ASIN {asin}</h3>
            {data?.found && (
              <div className={styles.pageSubtitle}>
                {data.sku_states.length} SKU{data.sku_states.length === 1 ? '' : 's'}
                {data.value_minor != null && ` · ${INR(data.value_minor)} value`}
              </div>
            )}
          </div>
          <button onClick={onClose} className={styles.modalClose}>✕</button>
        </div>
        <div className={styles.modalBody}>
          {loading && <div className={styles.muted}>Loading…</div>}
          {error && <div className={styles.error}>{error}</div>}
          {data && !data.found && <div className={styles.muted}>{data.message}</div>}
          {data?.found && (
            <>
              <h4 className={styles.sectionTitle}>Per SKU (state per SKU, never blended)</h4>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>SKU</th><th>Channel</th>
                      <th>Stock-out</th><th>Turnover</th><th>Dead?</th>
                      <th>On hand</th><th>Cover</th><th>Velocity</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sku_states.map((s) => (
                      <tr key={s.sku}>
                        <td className={styles.mono}>{s.sku}
                          {s.seller_declared && <span className={styles.pillMuted}>seller-declared</span>}
                        </td>
                        <td>{s.fulfillment_channel}</td>
                        <td>{s.stock_out_state
                          ? <StateChip state={s.stock_out_state} /> : '—'}</td>
                        <td>{s.turnover_state
                          ? <StateChip state={s.turnover_state} /> : '—'}</td>
                        <td>{s.dead_state
                          ? <StateChip state={s.dead_state} /> : '—'}</td>
                        <td>{s.fulfillable + (s.reserved || 0)}</td>
                        <td>{s.days_of_coverage != null ? `${s.days_of_coverage}d` : '—'}</td>
                        <td>{s.velocity != null ? s.velocity.toFixed(2) : '—'}</td>
                        <td>{NEEDS_ORDER.includes(s.stock_out_state) && (
                          <GoLink to={`${RESTOCK}?sku=${encodeURIComponent(s.sku)}`}>Restock →</GoLink>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.assumptions}>
                Assumptions in effect:{' '}
                <Assumption label="Lead time FBA" value={`${data.assumptions.lead_time_days_fba}d`}
                            isDefault={data.assumptions.is_default.lead_time_days_fba} />
                <Assumption label="Safety buffer" value={`${data.assumptions.safety_buffer_days}d`}
                            isDefault={data.assumptions.is_default.safety_buffer_days} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Settings modal ──────────────────────────────────────────────────────────

const SettingsModal = ({ onClose, onSaved }) => {
  const authCtx = useContext(AuthContext);
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const body = await api.getSettings(authCtx.token);
        setState({ loading: false, data: body.data, error: null });
        setValues({
          lead_time_days_fba: body.data.lead_time_days_fba,
          lead_time_days_mfn: body.data.lead_time_days_mfn,
          safety_buffer_days: body.data.safety_buffer_days,
          ordering_window_days: body.data.ordering_window_days,
          replenishment_cycle_days: body.data.replenishment_cycle_days,
        });
      } catch (e) {
        setState({ loading: false, data: null, error: e.message });
      }
    })();
  }, [authCtx.token]);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings(authCtx.token, values);
      onSaved();
      onClose();
    } catch (e) {
      setState((s) => ({ ...s, error: e.message }));
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Inventory assumptions</h3>
          <button onClick={onClose} className={styles.modalClose}>✕</button>
        </div>
        {state.loading && <div>Loading…</div>}
        {state.error && <div className={styles.error}>{state.error}</div>}
        {state.data && (
          <div className={styles.modalBody}>
            <p className={styles.modalHint}>
              These drive Stock-Out state classification and Restock timing.
              Assumed values are used until you save an explicit override.
            </p>
            {[
              ['lead_time_days_fba', 'Lead time (FBA), days'],
              ['lead_time_days_mfn', 'Lead time (MFN), days'],
              ['safety_buffer_days', 'Safety buffer, days'],
              ['ordering_window_days', 'Ordering window, days'],
              ['replenishment_cycle_days', 'Replenishment cycle, days (drives MFN corridor)'],
            ].map(([k, label]) => (
              <label key={k} className={styles.field}>
                <span>{label}
                  {state.data.is_default[k] && <span className={styles.pillMuted}>assumed</span>}
                </span>
                <input type="number" min={0}
                  value={values[k] ?? ''}
                  onChange={(e) => setValues({ ...values, [k]: Number(e.target.value) })}
                />
              </label>
            ))}
            <div className={styles.modalActions}>
              <button className={styles.btnPrimary} onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className={styles.btnGhost} onClick={onClose}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview', Component: OverviewPanel },
  { id: 'current-levels', label: 'Current Levels', Component: CurrentLevelsPanel },
  { id: 'stock-out-risk', label: 'Stock-Out Risk', Component: StockOutRiskPanel },
  { id: 'days-on-hand', label: 'Days on Hand', Component: DaysOnHandPanel },
  { id: 'turnover', label: 'Turnover', Component: TurnoverPanel },
  { id: 'dead-stock', label: 'Dead Stock', Component: DeadStockPanel },
  { id: 'value', label: 'Value Summary', Component: ValueReportPanel },
];

// Overview slice → tab id map (checklist #67 depth-2 filter-by-slice).
const SLICE_TAB_MAP = {
  'stock-out-risk': 'stock-out-risk',
  'dead-stock': 'dead-stock',
  'turnover': 'turnover',
  'current-levels': 'current-levels',
  'days-on-hand': 'days-on-hand',
  'value': 'value',
};

const InventoryHealth = () => {
  const authCtx = useContext(AuthContext);
  // #69 URL-driven state: tab + state-filter + sort persist in the URL
  // so deep-links and back-nav Just Work.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const stateFilter = searchParams.get('state') || undefined;
  const sortParam = searchParams.get('sort') || undefined;
  const [showSettings, setShowSettings] = useState(false);
  const [openAsin, setOpenAsin] = useState(searchParams.get('asin'));
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const active = useMemo(() => TABS.find((t) => t.id === activeTab), [activeTab]);
  const openSettings = () => setShowSettings(true);
  const closeSettings = () => setShowSettings(false);
  const onSettingsSaved = () => setReloadKey((k) => k + 1);

  // Hard-refresh: bust the Overview cache first (if we're on overview),
  // then bump reloadKey so the active tab re-fetches fresh data.
  const hardRefresh = async () => {
    setRefreshing(true);
    try {
      if (activeTab === 'overview' && authCtx?.token) {
        await api.getOverview(authCtx.token, { refresh: true });
      }
    } catch {
      // best-effort — fall through to reloadKey bump anyway
    } finally {
      setReloadKey((k) => k + 1);
      setRefreshing(false);
    }
  };

  const setTab = (tabId, extra = {}) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    // Merge or clear filter/sort
    if ('state' in extra) {
      if (extra.state) next.set('state', extra.state); else next.delete('state');
    }
    if ('sort' in extra) {
      if (extra.sort) next.set('sort', extra.sort); else next.delete('sort');
    }
    setSearchParams(next);
  };

  // #67 Overview slice click → jump to owning tab AND propagate the
  // clicked state (critical / warning / dead-stock / etc) as a filter.
  const onSliceClick = useCallback((filter, routeTo) => {
    const tabId = SLICE_TAB_MAP[routeTo] || routeTo;
    setTab(tabId, { state: filter?.state });
  }, [searchParams]);   // eslint-disable-line react-hooks/exhaustive-deps

  const ActiveComponent = active.Component;

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Inventory Health Monitor</h2>
          <p className={styles.pageSubtitle}>
            Shared risk model — Restock, Predictive and Seasonal all consume this.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={styles.btnGhost}
                  onClick={hardRefresh} disabled={refreshing}>
            {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
          </button>
          <button className={styles.btnGhost} onClick={openSettings}>
            Assumptions
          </button>
        </div>
      </div>

      <div className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.id}
            className={`${styles.tab} ${t.id === activeTab ? styles.tabActive : ''}`}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className={styles.tabBody} key={`${activeTab}-${stateFilter || 'all'}-${reloadKey}`}>
        <ActiveComponent openSettings={openSettings}
                         onSliceClick={onSliceClick}
                         onOpenAsin={setOpenAsin}
                         initialStateFilter={stateFilter}
                         initialSort={sortParam} />
      </div>

      {showSettings && (
        <SettingsModal onClose={closeSettings} onSaved={onSettingsSaved} />
      )}
      {openAsin && (
        <AsinDrawer asin={openAsin} onClose={() => setOpenAsin(null)} />
      )}
    </div>
  );
};

export default InventoryHealth;
